import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Modal,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
  HomeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import StatusSelect from '../components/StatusSelect';
import EntityCommentsCard from '../components/EntityCommentsCard';
import AttachmentPreview from '../components/AttachmentPreview';
import CertificateFormModal from '../modals/CertificateFormModal';
import DocumentFormModal from '../modals/DocumentFormModal';
import {
  OFFER_STATUSES,
  ORDER_STATUSES,
  getOfferStatusTone,
  getOrderStatusTone,
  getQuoteStatusTone,
  TYPE_TONES,
  DEFAULT_TONE,
} from '../constants/statuses';
import { useI18n } from '../i18n';
import { formatDate, formatDateTime } from '../utils/formatters';
import { brandSemantic } from '../config/brand';
import { getCertificateStatus, getDaysUntilExpiry } from '../utils/certificateHelpers';
import { derivedSupplierTypes } from '../utils/supplierHelpers';
import { LOGISTICS_MODULE_ENABLED } from '../constants/featureFlags';

const { confirm } = Modal;

const CERTIFICATE_STATUS_TONES = {
  Expired: { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
  'Expiring Soon': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Valid: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
};

const SUPPLIER_PRODUCT_PRICE_COLUMN_LABELS = {
  avgItemPrice: 'Average Item Price',
  avgProductionPrice: 'Average Production Price',
  avgBrandingPrice: 'Average Branding Price',
  avgDeliveryPricePerItemPerKm: 'Average Delivery Price per Item per Kilometer',
};

export default function SupplierProfilePage({
  selectedSupplier,
  returnView = 'suppliers',
  contacts,
  contactPersons,
  supplierProducts,
  offers,
  quotes = [],
  leads,
  certificates = [],
  onAddCertificate,
  onEditCertificate,
  onDeleteCertificate,
  documents = [],
  onAddDocument,
  onEditDocument,
  onDeleteDocument,
  products = [],
  categories = [],
  services = [],
  logisticsItems = [],
  currentUser,
  showModal,
  setSelectedSupplier,
  setCurrentView,
  openOrderDetails,
  openQuoteDetail,
  openLeadProfile,
  getOfferSupplierSections,
  getFollowUpsForOffer,
  getOverdueCount,
  getOfferStatus,
  updateOfferStatus,
  getLatestComment,
  openCommentsModal,
  setEditingSupplierProduct,
  onDeleteSupplierProduct,
  canDelete = true,
}) {
  const { t } = useI18n();
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [previewCertificate, setPreviewCertificate] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);

  if (!selectedSupplier) return null;

  const supplierContacts = contacts.filter((contact) => contact.clientId === selectedSupplier.id);
  const supplierContactPersons = contactPersons.filter((person) => person.entityId === selectedSupplier.id);
  const allSupplierProductRecords = supplierProducts.filter((sp) => sp.supplierId === selectedSupplier.id);
  // Supplier Products stays Supplier_Product-based (avgItemPrice — the same
  // catalogue the Selection_Wizard's Products step reads). Services and
  // Logistics, though, are each supplier's own dedicated catalogue entries
  // (initialServiceItems / initialLogisticsItems — the same data the
  // Products Module's Services/Logistics tabs show and the wizard's
  // Services/Logistics steps now source from), not Supplier_Product rows.
  const supplierProductRecords = allSupplierProductRecords.filter((sp) => sp.avgItemPrice != null);
  const supplierServiceRecords = services.filter((service) => service.supplierId === selectedSupplier.id);
  const supplierLogisticsRecords = logisticsItems.filter((item) => item.supplierId === selectedSupplier.id);
  const supplierCertificates = [...certificates]
    .filter((cert) => cert.supplierId === selectedSupplier.id)
    .map((cert) => ({
      ...cert,
      status: getCertificateStatus(cert.expiryDate),
      daysUntilExpiry: getDaysUntilExpiry(cert.expiryDate),
    }))
    .sort((a, b) => new Date(b.expiryDate) - new Date(a.expiryDate));
  const supplierDocuments = [...documents]
    .filter((doc) => doc.supplierId === selectedSupplier.id)
    .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
  const supplierOffers = offers.filter((offer) => (
    getOfferSupplierSections(offer.id).some((section) => section.name === selectedSupplier.name)
  ));
  const supplierInternalQuotes = quotes.filter((quote) => (
    (quote.suppliers || []).some((supplier) => supplier.supplierName === selectedSupplier.name)
  ));
  const supplierSalesQuotes = supplierOffers.filter((offer) => offer.type === 'Offer');
  const supplierOrders = supplierOffers.filter((offer) => offer.type === 'Order');
  const supplierQuotesTabData = [...supplierInternalQuotes, ...supplierSalesQuotes].sort((a, b) => (
    new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
  ));

  const clientNameColumn = {
    title: t('Client Name'),
    dataIndex: 'client',
    key: 'client',
    render: (text, record) => {
      const lead = leads.find((item) => item.id === record.clientId);
      return (
        <Button
          type="link"
          onClick={() => { if (lead) openLeadProfile(lead, 'leads'); }}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      );
    },
  };

  const followUpsColumn = {
    title: t('Follow-ups'),
    key: 'followUps',
    width: 110,
    align: 'center',
    render: (_, record) => {
      const items = getFollowUpsForOffer(record.id);
      const overdue = getOverdueCount(record.id);
      if (items.length === 0) {
        return (
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            style={{ padding: 0 }}
            onClick={() => showModal('viewFollowUps', record, { startAdding: true })}
          >
            {t('Add')}
          </Button>
        );
      }
      return (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => showModal('viewFollowUps', record)}
        >
          <Space size={4} align="center">
            <BellOutlined style={{ color: brandSemantic.primary, fontSize: 14, lineHeight: 1 }} />
            <span style={{ fontWeight: 600 }}>{items.length}</span>
            {overdue > 0 && (
              <SoftTag
                tone={{ color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder }}
                style={{ minHeight: 20, lineHeight: '18px', padding: '0 6px', fontSize: 11 }}
              >
                !{overdue}
              </SoftTag>
            )}
          </Space>
        </Button>
      );
    },
  };

  const quoteRecordColumns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 135,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => (
            record.type === 'Quote'
              ? openQuoteDetail(record)
              : openOrderDetails(record, 'supplierProfile')
          )}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type) => (
        <SoftTag tone={TYPE_TONES[type] || DEFAULT_TONE}>
          {t(type === 'Quote' ? 'Internal Quote' : 'Sales Quote')}
        </SoftTag>
      ),
    },
    clientNameColumn,
    {
      title: t('Amount'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (value) => (value ? <span style={{ fontWeight: 500 }}>{value}</span> : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    followUpsColumn,
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => {
        if (record.type === 'Quote') {
          return <SoftTag tone={getQuoteStatusTone(status)}>{t(status)}</SoftTag>;
        }
        const current = getOfferStatus(record.id, status);
        return (
          <StatusSelect
            value={current}
            onChange={(value) => updateOfferStatus(record.id, value)}
            statuses={OFFER_STATUSES}
            getTone={getOfferStatusTone}
            width={Math.min(Math.max(current.length * 7 + 34, 120), 260)}
          />
        );
      },
    },
  ];

  const orderColumns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 135,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => openOrderDetails(record, 'supplierProfile')}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    clientNameColumn,
    {
      title: t('Amount'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>,
    },
    followUpsColumn,
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => {
        const current = getOfferStatus(record.id, status);
        return (
          <StatusSelect
            value={current}
            onChange={(value) => updateOfferStatus(record.id, value)}
            statuses={ORDER_STATUSES}
            getTone={getOrderStatusTone}
            width={Math.min(Math.max(current.length * 7 + 34, 120), 260)}
          />
        );
      },
    },
  ];

  const contactPersonColumns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('Position'),
      dataIndex: 'position',
      key: 'position',
      render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Email'),
      dataIndex: 'email',
      key: 'email',
      render: (value) => value ? <a href={`mailto:${value}`}>{value}</a> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (value) => value ? <a href={`tel:${value}`}>{value}</a> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          style={{ padding: 0, height: 'auto' }}
          onClick={() => showModal('editContactPerson', record)}
        >
          {t('Edit')}
        </Button>
      ),
    },
  ];

  const supplierContactColumns = [
    {
      title: t('Contact Date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(b.date) - new Date(a.date),
      defaultSortOrder: 'ascend',
      render: (date) => formatDate(date),
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => {
        const colors = { Phone: 'blue', Email: 'green', Other: 'orange' };
        return <Tag color={colors[type]}>{t(type)}</Tag>;
      },
    },
    {
      title: t('Subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: (text, record) => (
        <Button type="link" onClick={() => showModal('viewContact', record)} style={{ padding: 0 }}>
          {text}
        </Button>
      ),
    },
    {
      title: t('Contact By'),
      dataIndex: 'userName',
      key: 'userName',
      width: 150,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => showModal('editContact', record)}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete Contact Record'),
                  content: t('Are you sure you want to delete this entry?'),
                  onOk() { message.success(t('Contact record deleted')); },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Supplier Products columns (Supplier_Product-based — avgItemPrice /
  // avgProductionPrice, the same catalogue the Selection_Wizard's Products
  // step reads).
  const buildSupplierCatalogColumns = (priceFields, deleteConfirmTitle) => [
    {
      title: t('Product Name'),
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: t('Category'),
      key: 'categoryName',
      width: 150,
      render: (_, record) => {
        const category = categories.find((c) => c.id === record.categoryId);
        return category?.name || <span style={{ color: '#bfbfbf' }}>—</span>;
      },
    },
    {
      title: t('Supplier Item Number'),
      dataIndex: 'supplierItemNumber',
      key: 'supplierItemNumber',
      width: 140,
      render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    ...priceFields.map((field) => ({
      title: t(SUPPLIER_PRODUCT_PRICE_COLUMN_LABELS[field]),
      dataIndex: field,
      key: field,
      width: 150,
      align: 'right',
      render: (value) => (value != null ? `${Number(value).toLocaleString('da-DK')} DKK` : <span style={{ color: '#bfbfbf' }}>—</span>),
    })),
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <SoftTag
          tone={status === 'Active'
            ? { color: '#6A8F6B', background: '#F2FBF2', border: '#B8E0B9' }
            : { color: '#8B6B6B', background: '#F8F3F1', border: '#D8C8C2' }}
        >
          {t(status)}
        </SoftTag>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setEditingSupplierProduct(record);
              showModal('editSupplierProduct');
            }}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: deleteConfirmTitle,
                  icon: <ExclamationCircleOutlined />,
                  content: t('Are you sure you want to delete this entry?'),
                  onOk() { onDeleteSupplierProduct(record.id); },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const supplierProductColumns = buildSupplierCatalogColumns(['avgItemPrice', 'avgProductionPrice'], t('Delete Supplier Product'));

  // Supplier Services / Logistics columns — the same columns the Products
  // Module's Services/Logistics tabs show (Code, Name, Service Type /
  // Pricing Unit, Status), minus the Supplier column (redundant here — this
  // whole tab is already scoped to one supplier) and Actions (managed from
  // the Products Module, not from here).
  const catalogStatusTag = (status) => (
    <SoftTag
      tone={status === 'Active'
        ? { color: '#6A8F6B', background: '#F2FBF2', border: '#B8E0B9' }
        : { color: '#8B6B6B', background: '#F8F3F1', border: '#D8C8C2' }}
    >
      {t(status)}
    </SoftTag>
  );

  const supplierServiceColumns = [
    { title: t('Code'), dataIndex: 'code', key: 'code', width: 110 },
    { title: t('Service Name'), dataIndex: 'name', key: 'name', render: (name) => t(name) },
    {
      title: t('Service Type'),
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 130,
      render: (serviceType) => <Tag color={serviceType === 'Charge' ? 'gold' : 'geekblue'}>{t(serviceType)}</Tag>,
    },
    { title: t('Pricing Unit'), dataIndex: 'pricingUnit', key: 'pricingUnit', width: 130, render: (unit) => t(unit) },
    { title: t('Status'), dataIndex: 'status', key: 'status', width: 120, render: catalogStatusTag },
  ];

  const supplierLogisticsColumns = [
    { title: t('Code'), dataIndex: 'code', key: 'code', width: 110 },
    { title: t('Logistics Item Name'), dataIndex: 'name', key: 'name', render: (name) => t(name) },
    { title: t('Pricing Unit'), dataIndex: 'pricingUnit', key: 'pricingUnit', width: 130, render: (unit) => t(unit) },
    { title: t('Status'), dataIndex: 'status', key: 'status', width: 120, render: catalogStatusTag },
  ];

  const certificateColumns = [
    {
      title: t('Document / Certificate'),
      key: 'document',
      render: (_, record) => (
        <Space align="start">
          <FilePdfOutlined style={{ color: brandSemantic.error, fontSize: 20, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>{record.fileName}</span>
          </div>
        </Space>
      ),
    },
    {
      title: t('Type / Standard'),
      key: 'typeStandard',
      render: (_, record) => (
        <div>
          <div>{record.documentType || <span style={{ color: '#bfbfbf' }}>—</span>}</div>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>{record.standard || '—'}</span>
        </div>
      ),
    },
    {
      title: t('Valid From'),
      dataIndex: 'validFrom',
      key: 'validFrom',
      width: 120,
      render: (value) => (value ? formatDate(value) : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Valid To'),
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (value) => (value ? formatDate(value) : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <SoftTag tone={CERTIFICATE_STATUS_TONES[status] || CERTIFICATE_STATUS_TONES.Valid}>{t(status)}</SoftTag>,
    },
    {
      title: t('Expires In'),
      key: 'expiresIn',
      width: 110,
      render: (_, record) => (
        record.daysUntilExpiry != null
          ? <span style={{ color: (CERTIFICATE_STATUS_TONES[record.status] || CERTIFICATE_STATUS_TONES.Valid).color, fontWeight: 600 }}>{t('{{count}} days', { count: record.daysUntilExpiry })}</span>
          : <span style={{ color: '#bfbfbf' }}>—</span>
      ),
    },
    {
      title: t('Uploaded By'),
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 150,
      render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setPreviewCertificate({ url: record.fileUrl, name: record.fileName })}
          >
            {t('Preview')}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setEditingCertificate(record);
              setIsCertificateModalOpen(true);
            }}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete Certificate'),
                  icon: <ExclamationCircleOutlined />,
                  content: t('Are you sure you want to delete "{{name}}"?', { name: record.name }),
                  onOk() {
                    onDeleteCertificate(record.id);
                    message.success(t('Certificate deleted'));
                  },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleCertificateSubmit = (certData) => {
    const success = editingCertificate
      ? onEditCertificate(certData)
      : onAddCertificate(certData);
    if (success !== false) {
      message.success(t(editingCertificate ? 'Certificate updated' : 'Certificate added'));
      setIsCertificateModalOpen(false);
      setEditingCertificate(null);
    }
    return success;
  };

  const documentColumns = [
    { title: t('Document Name'), dataIndex: 'name', key: 'name' },
    { title: t('Description'), dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: t('Uploaded File'),
      key: 'file',
      render: (_, record) => (
        <AttachmentPreview files={[{ uid: record.id, name: record.fileName, url: record.fileUrl }]} />
      ),
    },
    { title: t('Uploaded By'), dataIndex: 'uploadedBy', key: 'uploadedBy', width: 150 },
    { title: t('Upload Date'), dataIndex: 'uploadDate', key: 'uploadDate', width: 150, render: (value) => formatDate(value) },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setPreviewCertificate({ url: record.fileUrl, name: record.fileName })}
          >
            {t('Preview')}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setEditingDocument(record);
              setIsDocumentModalOpen(true);
            }}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete Document'),
                  icon: <ExclamationCircleOutlined />,
                  content: t('Are you sure you want to delete "{{name}}"?', { name: record.name }),
                  onOk() {
                    onDeleteDocument(record.id);
                    message.success(t('Document deleted'));
                  },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleDocumentSubmit = (documentData) => {
    const success = editingDocument
      ? onEditDocument(documentData)
      : onAddDocument(documentData);
    if (success !== false) {
      message.success(t(editingDocument ? 'Document updated' : 'Document added'));
      setIsDocumentModalOpen(false);
      setEditingDocument(null);
    }
    return success;
  };

  return (
    <div>
      <Button
        icon={<HomeOutlined />}
        onClick={() => {
          setSelectedSupplier(null);
          setCurrentView(returnView);
        }}
        style={{ marginBottom: '16px' }}
      >
        {t('Back to List')}
      </Button>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={24}>
          <Col span={12}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label={t('Supplier Name')}>
                <strong>{selectedSupplier.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="CVR">{selectedSupplier.cvr}</Descriptions.Item>
              <Descriptions.Item label={t('Status')}>
                <Tag color={selectedSupplier.status === 'Active' ? 'green' : 'orange'}>
                  {t(selectedSupplier.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Visibility')}>
                <Tag color={(selectedSupplier.visibility || 'Public') === 'Private' ? 'purple' : 'cyan'}>
                  {t(selectedSupplier.visibility || 'Public')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Supplier Type')}>
                {(() => {
                  const types = derivedSupplierTypes(selectedSupplier.id, supplierProducts, services, logisticsItems);
                  return types.length > 0
                    ? <Space size={4} wrap>{types.map((type) => <Tag key={type}>{t(type)}</Tag>)}</Space>
                    : <span style={{ color: '#bfbfbf' }}>—</span>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label={t('Country')}>{t(selectedSupplier.country)}</Descriptions.Item>
              <Descriptions.Item label={t('Default Currency')}>{t(selectedSupplier.currency)}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label={t('Address')}>{selectedSupplier.address}</Descriptions.Item>
              <Descriptions.Item label={t('Email')}>{selectedSupplier.email}</Descriptions.Item>
              <Descriptions.Item label={t('Phone')}>{selectedSupplier.phone}</Descriptions.Item>
              <Descriptions.Item label={t('Website')}>
                {selectedSupplier.website
                  ? <a href={selectedSupplier.website} target="_blank" rel="noopener noreferrer">{selectedSupplier.website}</a>
                  : <span style={{ color: '#bfbfbf' }}>—</span>}
              </Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: '16px 0' }} />
            <EntityCommentsCard
              entityId={selectedSupplier.id}
              entityName={selectedSupplier.name}
              getLatestComment={getLatestComment}
              formatDateTime={formatDateTime}
              onOpenComments={openCommentsModal}
            />
          </Col>
        </Row>
        <Divider />
        <Tabs
          items={[
            {
              key: '0',
              label: t('Contact Records'),
              children: (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, marginBottom: 12 }}
                    onClick={() => showModal('createContact')}
                  >
                    {t('Add Contact Record')}
                  </Button>
                  <Table
                    columns={supplierContactColumns}
                    dataSource={supplierContacts}
                    rowKey="id"
                    pagination={false}
                    bordered
                    size="middle"
                  />
                </>
              ),
            },
            {
              key: '1',
              label: t('Contact Persons'),
              children: (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, marginBottom: 12 }}
                    onClick={() => showModal('createContactPerson')}
                  >
                    {t('Add Contact Person')}
                  </Button>
                  <Table
                    columns={contactPersonColumns}
                    dataSource={supplierContactPersons}
                    rowKey="id"
                    pagination={false}
                    bordered
                    size="middle"
                  />
                </>
              ),
            },
            {
              key: '2',
              label: t('Supplier Products'),
              children: (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, marginBottom: 12 }}
                    onClick={() => {
                      setEditingSupplierProduct(null);
                      showModal('createSupplierProduct');
                    }}
                  >
                    {t('Add Supplier Product')}
                  </Button>
                  <Table
                    columns={supplierProductColumns}
                    dataSource={supplierProductRecords}
                    rowKey="id"
                    pagination={false}
                    bordered
                    size="middle"
                    locale={{ emptyText: t('No products assigned yet') }}
                  />
                </>
              ),
            },
            {
              key: '2a',
              label: t('Supplier Services'),
              children: (
                <Table
                  columns={supplierServiceColumns}
                  dataSource={supplierServiceRecords}
                  rowKey="id"
                  pagination={false}
                  bordered
                  size="middle"
                  locale={{ emptyText: t('No services assigned yet') }}
                />
              ),
            },
            // Supplier Logistics tab soft-removed (LOGISTICS_MODULE_ENABLED)
            // — data/columns above are untouched so it can come back by
            // flipping the flag.
            LOGISTICS_MODULE_ENABLED && {
              key: '2b',
              label: t('Supplier Logistics'),
              children: (
                <Table
                  columns={supplierLogisticsColumns}
                  dataSource={supplierLogisticsRecords}
                  rowKey="id"
                  pagination={false}
                  bordered
                  size="middle"
                  locale={{ emptyText: t('No logistics items assigned yet') }}
                />
              ),
            },
            {
              key: '3',
              label: t('Quotes'),
              children: (
                <Table
                  columns={quoteRecordColumns}
                  dataSource={supplierQuotesTabData}
                  rowKey="id"
                  pagination={false}
                  bordered
                  size="middle"
                />
              ),
            },
            {
              key: '4',
              label: t('Orders'),
              children: (
                <Table
                  columns={orderColumns}
                  dataSource={supplierOrders}
                  rowKey="id"
                  pagination={false}
                  bordered
                  size="middle"
                />
              ),
            },
            {
              key: '5',
              label: t('Certificates'),
              children: (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, marginBottom: 12 }}
                    onClick={() => {
                      setEditingCertificate(null);
                      setIsCertificateModalOpen(true);
                    }}
                  >
                    {t('Add Certificate')}
                  </Button>
                  {supplierCertificates.length === 0 ? (
                    <Empty description={t('No certificates yet')} />
                  ) : (
                    <Table
                      columns={certificateColumns}
                      dataSource={supplierCertificates}
                      rowKey="id"
                      pagination={false}
                      bordered
                      size="middle"
                    />
                  )}
                </>
              ),
            },
            {
              key: '6',
              label: t('Documents'),
              children: (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ padding: 0, marginBottom: 12 }}
                    onClick={() => {
                      setEditingDocument(null);
                      setIsDocumentModalOpen(true);
                    }}
                  >
                    {t('Add Document')}
                  </Button>
                  {supplierDocuments.length === 0 ? (
                    <Empty description={t('No documents yet')} />
                  ) : (
                    <Table
                      columns={documentColumns}
                      dataSource={supplierDocuments}
                      rowKey="id"
                      pagination={false}
                      bordered
                      size="middle"
                    />
                  )}
                </>
              ),
            },
          ].filter(Boolean)}
        />
      </Card>

      <Modal
        title={previewCertificate?.name}
        open={Boolean(previewCertificate)}
        onCancel={() => setPreviewCertificate(null)}
        footer={null}
        width="80%"
        styles={{ body: { padding: 0, height: '80vh' } }}
        destroyOnClose
      >
        {previewCertificate && (
          <iframe
            src={previewCertificate.url}
            title={previewCertificate.name}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </Modal>

      <CertificateFormModal
        open={isCertificateModalOpen}
        onCancel={() => {
          setIsCertificateModalOpen(false);
          setEditingCertificate(null);
        }}
        onSubmit={handleCertificateSubmit}
        editingCertificate={editingCertificate}
        supplier={selectedSupplier}
        products={products}
        currentUser={currentUser}
      />

      <DocumentFormModal
        open={isDocumentModalOpen}
        onCancel={() => {
          setIsDocumentModalOpen(false);
          setEditingDocument(null);
        }}
        onSubmit={handleDocumentSubmit}
        editingDocument={editingDocument}
        supplier={selectedSupplier}
        currentUser={currentUser}
      />
    </div>
  );
}
